import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import {
  formatChallengeDeadlineLabel,
  formatChallengeDeadlineTooltip,
  isChallengeDeadlineUrgent,
} from '../challenge-deadline.util';
// Share feature: commented out for now. Uncomment to re-enable.
// import { CardShareButtonComponent } from '../../../shared/components/card-share-button/card-share-button.component';

const IMAGE_WIDTH_CONTAIN_THRESHOLD = 1000;
const IMAGE_WIDTH_INHERIT_THRESHOLD = 160;

export interface ChallengeCardData {
  id: string;
  name: string;
  description?: string;
  /** Subtitle from API, shown below title (replaces provider/MahaAgX Team). */
  provider?: string;
  imageUrl?: string;
  participants: number;
  submissions: number;
  prizeMoney: number;
  /** Currency code from API (e.g. INR, USD). When INR, $ is not shown. */
  currency?: string;
  daysRemaining: number;
  /** ISO date string of submission deadline. Used to show "Xhr left" when deadline is today. */
  endDate?: string;
  /** Submission start date (YYYY-MM-DD). */
  submissionStartsAt?: string;
  /** Submission end date (YYYY-MM-DD). */
  submissionEndsAt?: string;
  /** Evaluation end date (YYYY-MM-DD). */
  evaluationEndsAt?: string;
  link?: string;
}

@Component({
  selector: 'app-challenge-card',
  standalone: true,
  imports: [CommonModule /* , CardShareButtonComponent */], // Share feature: uncomment to re-enable
  templateUrl: './challenge-card.component.html',
  styleUrl: './challenge-card.component.scss',
})
export class ChallengeCardComponent implements OnChanges {
  @Input() data!: ChallengeCardData;
  @Input() variant: 'default' | 'alternative' | 'minimal' = 'minimal'; // Default to minimal
  @Input() showDescription: boolean = false; // Control description visibility
  @Input() disableNavigation: boolean = false; // Disable click navigation
  /** Override object-fit. When not set, uses width>1000px → contain, else → cover. */
  @Input() imageObjectFit?: 'cover' | 'contain';

  /** Image natural width, set on load. Used for auto object-fit. */
  imageWidth: number | null = null;

  private googleAnalytics = inject(GoogleAnalyticsService);

  /** Use contain when: explicit 'contain' OR (no override and image width > 1000px). */
  get useImageContain(): boolean {
    if (this.imageObjectFit === 'cover') return false;
    if (this.imageObjectFit === 'contain') return true;
    if (this.useImageInherit) return false;
    return this.imageWidth != null && this.imageWidth > IMAGE_WIDTH_CONTAIN_THRESHOLD;
  }

  /** Use inherit when: no override and image width < 160px. */
  get useImageInherit(): boolean {
    if (this.imageObjectFit) return false;
    return this.imageWidth != null && this.imageWidth < IMAGE_WIDTH_INHERIT_THRESHOLD;
  }

  /** True when currency is USD (show $ icon). Hide $ icon for INR. */
  get isUsdCurrency(): boolean {
    return this.data?.currency?.toUpperCase() === 'USD';
  }

  /** True when showing hours (< 24hr) – for urgent deadline highlight. */
  get isHoursLeft(): boolean {
    return isChallengeDeadlineUrgent(this.data);
  }

  constructor(private router: Router) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']?.currentValue?.imageUrl !== changes['data']?.previousValue?.imageUrl) {
      this.imageWidth = null;
    }
  }

  onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img?.naturalWidth) {
      this.imageWidth = img.naturalWidth;
    }
  }

  /** Currency symbol for prize (₹ or $). */
  getPrizeSymbol(): string {
    const currency = this.data?.currency?.toUpperCase();
    if (currency === 'USD') return '$';
    return '₹';
  }

  /** Formatted prize amount (number only, no symbol). */
  getPrizeAmount(amount: number): string {
    return amount.toLocaleString('en-IN');
  }

  formatPrizeMoney(amount: number): string {
    return `${this.getPrizeSymbol()}${this.getPrizeAmount(amount)}`;
  }

  getDeadlineLabel(): string {
    return formatChallengeDeadlineLabel(this.data);
  }

  getImageUrl(): string {
    // Use provided image URL or default placeholder
    return this.data.imageUrl || 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=400&h=200&fit=crop';
  }

  /** Format date for submission display (11th Feb 26). */
  formatDisplayDate(dateStr: string): string {
    if (!dateStr) return '';
    const trimmed = dateStr.trim();
    const d = /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
      ? (() => {
          const [year, month, day] = trimmed.split('-').map(Number);
          return new Date(year, month - 1, day);
        })()
      : new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = d.getDate();
    const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[d.getMonth()];
    const year = String(d.getFullYear()).slice(-2);
    return `${day}${suffix} ${month} ${year}`;
  }

  /** Tooltip: submission end date while open, evaluation end date after submission closes. */
  getDeadlineTooltip(): string {
    return formatChallengeDeadlineTooltip(this.data, (date) => this.formatDisplayDate(date));
  }

  navigateToDetail(): void {
    if (this.data.id) {
      this.googleAnalytics.trackChallengeClick(this.data.name ?? '', this.data.id);
      this.router.navigate(['/challenges', this.data.id]);
    }
  }
}

